import {
  ModuleRegistry,
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowClickedEvent,
  RowStyleModule,
  SortChangedEvent
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import EndTreatmentGroupModal from '@/components/EndTreatmentGroupModal';
import MoveGroupMembersModal from '@/components/MoveGroupMembersModal';
import PageHeader from '@/components/PageHeader';
import TreatmentGroupView from '@/components/TreatmentGroupView';
import { TREATMENT_GROUP_VIEW_PARAM } from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import TreatmentGroupForm from '@/forms/TreatmentGroupForm';
import { useGroupAppointmentTypeOptions } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import useJurisdictions from '@/hooks/useJurisdictions';
import usePagination from '@/hooks/usePagination';
import useStaff from '@/hooks/useStaff';
import useTreatmentGroups, {
  useDeleteTreatmentGroup,
  useTreatmentGroup
} from '@/hooks/useTreatmentGroups';
import { DIRECTION } from '@/types/sort';
import { TreatmentGroup } from '@/types/treatmentGroup';
import { Alert, Button, Drawer, Input, PillSwitch, PillSwitchOption, Select } from '@/ui';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import { OptionType } from '@/ui/components/Form/Select/types';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import { resolveGridTheme } from '@/utils/gridTheme';
import {
  hasPermission,
  TREATMENT_GROUP_ADD,
  TREATMENT_GROUP_CHANGE,
  TREATMENT_GROUP_DELETE
} from '@/utils/userPermission';

import { COLUMN_ORDERING_MAP, createColumnDefs } from './columns';

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowAutoHeightModule,
  RowStyleModule
]);

export const treatmentGroupsDefaults = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1'
};

const ACCEPTING_NEW_PATIENTS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const DRAFTS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

const ENDED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
];

// Ended groups are hidden unless the user opts in, so a missing param means 'false'.
const DEFAULT_ENDED = 'false';

const TreatmentGroups = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { addMessage, permissions, resolvedTheme, darkStyle } = useContext(AppContext);

  const gridTheme = useMemo(
    () => resolveGridTheme(resolvedTheme, darkStyle),
    [resolvedTheme, darkStyle]
  );

  const canAdd = hasPermission({ permissions, requiredPermission: TREATMENT_GROUP_ADD });
  const canChange = hasPermission({ permissions, requiredPermission: TREATMENT_GROUP_CHANGE });
  const canDelete = hasPermission({ permissions, requiredPermission: TREATMENT_GROUP_DELETE });

  const deleteTreatmentGroup = useDeleteTreatmentGroup();

  // 'new' opens the drawer for a blank group; a group id opens it for that
  // row, fetched by id since the list response omits members; null means
  // the drawer is closed.
  const [drawerGroup, setDrawerGroup] = useState<string | 'new' | null>(null);
  const closeDrawer = () => setDrawerGroup(null);
  const editingGroupId = drawerGroup !== 'new' && drawerGroup !== null ? drawerGroup : undefined;
  const {
    data: editingGroupResponse,
    isPending: isEditingGroupPending,
    isError: isEditingGroupError
  } = useTreatmentGroup(editingGroupId);

  const handleDelete = (group: TreatmentGroup) => {
    if (!window.confirm(`Delete "${group.name}"? This cannot be undone.`)) return;
    deleteTreatmentGroup.mutate(group.id);
  };

  const [pendingEndGroup, setPendingEndGroup] = useState<TreatmentGroup | null>(null);
  const [pendingMoveMembersGroup, setPendingMoveMembersGroup] = useState<TreatmentGroup | null>(
    null
  );

  // The detail view opens in a drawer, keyed off a search param rather than
  // local state so a direct link/refresh/back-button all still work.
  const viewingGroupId = searchParams.get(TREATMENT_GROUP_VIEW_PARAM);
  const [isViewingGroupEditing, setIsViewingGroupEditing] = useState(false);
  const openGroupView = (groupId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(TREATMENT_GROUP_VIEW_PARAM, groupId);
      return next;
    });
  };
  const closeGroupView = () => {
    setIsViewingGroupEditing(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(TREATMENT_GROUP_VIEW_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  // AG Grid's row click listener is attached to the row element itself, an
  // ancestor of the action buttons -- it fires before any React onClick on
  // those buttons could stopPropagation, so the guard has to live here
  // instead, by inspecting what was actually clicked.
  const handleRowClicked = (event: RowClickedEvent<TreatmentGroup>) => {
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest('button, a')) return;
    if (!event.data) return;
    openGroupView(event.data.id);
  };

  const { data: appointmentTypeOptionsData } = useGroupAppointmentTypeOptions();
  const { data: staffData } = useStaff();
  const { data: jurisdictionsData } = useJurisdictions();

  const appointmentTypeOptions: OptionType<string>[] = (appointmentTypeOptionsData ?? []).map(
    (type) => ({ value: type.id, label: type.name })
  );

  const staffOptions: OptionType<string>[] = (staffData?.data ?? [])
    .map((staff) => ({
      value: staff.id,
      label: `${staff.first_name ?? ''} ${staff.last_name ?? ''}`.trim()
    }))
    .filter((option) => option.label);

  const jurisdictionOptions: OptionType<string>[] = (jurisdictionsData ?? []).map(
    (jurisdiction) => ({ value: jurisdiction.id, label: jurisdiction.name })
  );

  const activeParams = {
    direction: searchParams.get('direction') ?? treatmentGroupsDefaults.direction,
    ordering: searchParams.get('ordering') ?? treatmentGroupsDefaults.ordering,
    page: searchParams.get('page') ?? treatmentGroupsDefaults.page,
    appointment_type: searchParams.get('appointment_type') ?? '',
    accepting_new_patients: searchParams.get('accepting_new_patients') ?? '',
    counselor: searchParams.get('counselor') ?? '',
    jurisdiction: searchParams.get('jurisdiction') ?? '',
    is_draft: searchParams.get('is_draft') ?? '',
    is_ended: searchParams.get('is_ended') ?? DEFAULT_ENDED,
    search: searchParams.get('search') ?? ''
  };

  const appointmentTypeFilter: OptionType<string>[] = activeParams.appointment_type
    ? appointmentTypeOptions.filter((option) =>
        activeParams.appointment_type.split(',').includes(option.value)
      )
    : [];
  const counselorFilter: OptionType<string>[] = activeParams.counselor
    ? staffOptions.filter((option) => activeParams.counselor.split(',').includes(option.value))
    : [];
  const jurisdictionFilter: OptionType<string>[] = activeParams.jurisdiction
    ? jurisdictionOptions.filter((option) =>
        activeParams.jurisdiction.split(',').includes(option.value)
      )
    : [];

  const applyMultiFilter = (key: string, values: OptionType<string>[]) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const csv = values.map((v) => v.value).join(',');
      if (csv) next.set(key, csv);
      else next.delete(key);
      next.set('page', '1');
      return next;
    });
  };

  const handleAppointmentTypeChange = (values: OptionType<string>[]) =>
    applyMultiFilter('appointment_type', values);
  const handleCounselorChange = (values: OptionType<string>[]) =>
    applyMultiFilter('counselor', values);
  const handleJurisdictionChange = (values: OptionType<string>[]) =>
    applyMultiFilter('jurisdiction', values);

  const handleAcceptingNewPatientsChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('accepting_new_patients');
      else next.set('accepting_new_patients', value);
      next.set('page', '1');
      return next;
    });
  };

  const handleIsDraftChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'all') next.delete('is_draft');
      else next.set('is_draft', value);
      next.set('page', '1');
      return next;
    });
  };

  const handleIsEndedChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === DEFAULT_ENDED) next.delete('is_ended');
      else next.set('is_ended', value);
      next.set('page', '1');
      return next;
    });
  };

  const [searchInput, setSearchInput] = useState(activeParams.search);
  useEffect(() => {
    if (searchInput === activeParams.search) return;
    const timeout = setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.set('page', '1');
        return next;
      });
    }, 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      [
        'appointment_type',
        'accepting_new_patients',
        'counselor',
        'jurisdiction',
        'is_draft',
        'is_ended',
        'search'
      ].forEach((k) => next.delete(k));
      next.set('page', '1');
      return next;
    });
  };

  const hasActiveFilters =
    !!activeParams.appointment_type ||
    !!activeParams.accepting_new_patients ||
    !!activeParams.counselor ||
    !!activeParams.jurisdiction ||
    !!activeParams.is_draft ||
    activeParams.is_ended !== DEFAULT_ENDED ||
    !!activeParams.search;

  const handleSortChanged = (event: SortChangedEvent<TreatmentGroup>) => {
    const sortModel = event.api.getColumnState().find((col) => col.sort);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const colId = sortModel?.colId;
      if (colId && COLUMN_ORDERING_MAP[colId]) {
        next.set('ordering', COLUMN_ORDERING_MAP[colId]);
        next.set('direction', sortModel?.sort === 'asc' ? DIRECTION.ASC : DIRECTION.DESC);
      } else {
        next.set('ordering', treatmentGroupsDefaults.ordering);
        next.set('direction', treatmentGroupsDefaults.direction);
      }
      next.set('page', '1');
      return next;
    });
  };

  const {
    direction,
    ordering,
    page,
    appointment_type,
    accepting_new_patients,
    counselor,
    jurisdiction,
    is_draft,
    is_ended,
    search
  } = activeParams;
  const requestParams = {
    ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
    page,
    ...(appointment_type && { appointment_type }),
    ...(accepting_new_patients && { accepting_new_patients }),
    ...(counselor && { counselor }),
    ...(jurisdiction && { jurisdiction }),
    ...(is_draft && { is_draft }),
    ...(is_ended !== 'all' && { is_ended }),
    ...(search && { search })
  };

  const { data, isPending, isSuccess } = useTreatmentGroups(requestParams);

  const groups = data?.data?.results ?? [];
  const totalCount = data?.data?.count ?? 0;
  const totalPages = isSuccess ? (data.data?.total_pages ?? 0) : 0;

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    +activeParams.page,
    totalPages
  );

  useEffect(() => {
    if (currentPage !== +treatmentGroupsDefaults.page)
      setSearchParams((currentParams) => {
        currentParams.set('page', currentPage.toString());
        return currentParams;
      });
    else
      setSearchParams((currentParams) => {
        currentParams.delete('page');
        return currentParams;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <Container type="full-width-page">
      <div className="flex items-center justify-between mb-3">
        <PageHeader headingText="Treatment Groups" />
        {canAdd && <Button onClick={() => setDrawerGroup('new')}>New group</Button>}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
        <div className="flex flex-wrap items-start gap-3">
          <Input
            className="min-w-[180px] flex-1"
            label="Search"
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search groups or counselors…"
            value={searchInput}
          />
          <div className="relative min-w-[180px] flex-1">
            <Select
              multiple
              debounce={false}
              label="Appointment type"
              onChange={handleAppointmentTypeChange}
              options={appointmentTypeOptions}
              placeholder="All types"
              selectedOptions={appointmentTypeFilter}
            />
          </div>
          <div className="relative min-w-[180px] flex-1">
            <Select
              multiple
              debounce={false}
              label="Counselor"
              onChange={handleCounselorChange}
              options={staffOptions}
              placeholder="All counselors"
              selectedOptions={counselorFilter}
            />
          </div>
          <div className="relative min-w-[180px] flex-1">
            <Select
              multiple
              debounce={false}
              label="Jurisdiction"
              onChange={handleJurisdictionChange}
              options={jurisdictionOptions}
              placeholder="All jurisdictions"
              selectedOptions={jurisdictionFilter}
            />
          </div>
          <div className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Accepting new patients</span>
            <PillSwitch
              value={activeParams.accepting_new_patients || 'all'}
              onChange={handleAcceptingNewPatientsChange}>
              {ACCEPTING_NEW_PATIENTS_OPTIONS.map((option) => (
                <PillSwitchOption key={option.value} value={option.value}>
                  {option.label}
                </PillSwitchOption>
              ))}
            </PillSwitch>
          </div>
          <div className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Drafts</span>
            <PillSwitch value={activeParams.is_draft || 'all'} onChange={handleIsDraftChange}>
              {DRAFTS_OPTIONS.map((option) => (
                <PillSwitchOption key={option.value} value={option.value}>
                  {option.label}
                </PillSwitchOption>
              ))}
            </PillSwitch>
          </div>
          <div className="flex w-fit flex-col gap-1">
            <span className="text-sm font-medium text-gray-700">Ended</span>
            <PillSwitch value={activeParams.is_ended} onChange={handleIsEndedChange}>
              {ENDED_OPTIONS.map((option) => (
                <PillSwitchOption key={option.value} value={option.value}>
                  {option.label}
                </PillSwitchOption>
              ))}
            </PillSwitch>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-gray-500">
            {isPending ? 'Loading…' : `${totalCount} group${totalCount !== 1 ? 's' : ''}`}
          </span>
          {hasActiveFilters && (
            <button
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
              onClick={handleClearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {isPending && <Spinner />}
      <Card className="overflow-hidden p-0" paddingClass="p-0">
        <div className="w-full">
          <AgGridReact<TreatmentGroup>
            theme={gridTheme}
            animateRows={false}
            columnDefs={createColumnDefs({
              canDelete,
              canEdit: canChange,
              canEnd: canChange,
              canMoveMembers: canChange,
              onDelete: handleDelete,
              onEdit: (group) => setDrawerGroup(group.id),
              onEnd: setPendingEndGroup,
              onMoveMembers: setPendingMoveMembersGroup,
              onView: (group) => openGroupView(group.id)
            })}
            domLayout="autoHeight"
            onRowClicked={handleRowClicked}
            onSortChanged={handleSortChanged}
            pagination={false}
            rowClass="cursor-pointer"
            rowData={groups}
          />
        </div>
      </Card>

      <div className="mt-4">
        <Paginator
          currentPage={currentPage}
          goToPage={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          totalPages={totalPages}
        />
      </div>

      <Drawer
        open={drawerGroup !== null}
        onClose={closeDrawer}
        title={drawerGroup === 'new' ? 'Create Treatment Group' : 'Edit Treatment Group'}>
        {editingGroupId && isEditingGroupPending ? (
          <Spinner />
        ) : editingGroupId && isEditingGroupError ? (
          <Alert variant="error" message="Failed to load treatment group." />
        ) : (
          <TreatmentGroupForm
            onCancel={closeDrawer}
            onSuccess={closeDrawer}
            treatmentGroup={editingGroupResponse?.data}
          />
        )}
      </Drawer>

      <Drawer
        open={!!viewingGroupId}
        onClose={closeGroupView}
        blurredBackground
        dismissOnClickOutside={!isViewingGroupEditing}>
        {viewingGroupId && (
          <TreatmentGroupView
            key={viewingGroupId}
            onDeleted={closeGroupView}
            onEditingChange={setIsViewingGroupEditing}
            treatmentGroupId={viewingGroupId}
          />
        )}
      </Drawer>

      <EndTreatmentGroupModal group={pendingEndGroup} onClose={() => setPendingEndGroup(null)} />
      <MoveGroupMembersModal
        sourceGroup={pendingMoveMembersGroup}
        onClose={() => setPendingMoveMembersGroup(null)}
        onSuccess={(destinationGroup, copiedCount) => {
          setPendingMoveMembersGroup(null);
          addMessage({
            level: 'success',
            text: `${copiedCount} member${copiedCount === 1 ? '' : 's'} added to ${destinationGroup.name}`
          });
        }}
      />
    </Container>
  );
};

export default TreatmentGroups;
